<script>
import { clickOutsideMixin } from '@utils/click-outside-mixin'

// Largura mínima do painel aberto. Era literal no CSS (`min-width: 180px`);
// virou default da prop `panelWidth` para o seletor de agentes poder pedir
// 320px sem que os consumidores atuais mudem de tamanho.
const LARGURA_PAINEL_PADRAO = 180
// Altura de PIOR CASO do painel, para decidir o lado antes de ele existir:
// `max-height` da lista (280) + o padding dela (8+8) + a borda da casca.
const ALTURA_MAXIMA_PAINEL = 300

// Dropdown compacto e "camuflado" para a toolbar do composer do chat de IA
// (seletor de modelo/persona). Propositalmente NÃO usa v-menu do Vuetify:
// dentro do painel flutuante do chat popup o v-menu não fechava ao clicar
// fora (só fechava selecionando um item) — o clickOutsideMixin é
// determinístico e não depende do empilhamento de overlays do Vuetify.
export default {
  name: 'IntelligenceV2ComposerInlineSelect',
  mixins: [clickOutsideMixin],
  props: {
    label: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      default: '',
    },
    // null = deriva de `options` (ver triggerIconColor): activeColor quando
    // há uma opção ativa, neutro quando não há. Passar um valor explícito
    // (ex: a marca "W" do seletor de modelo) sempre tem prioridade sobre isso.
    iconColor: {
      type: String,
      default: null,
    },
    // Cor usada no ícone/check do item ativo dentro do menu E no trigger
    // fechado quando `iconColor` não foi passado (ver triggerIconColor).
    // Default `--primary` (branding white-label) — todos os seletores do
    // composer seguem a cor do escritório; nenhum fixa a marca AAWZ.
    activeColor: {
      type: String,
      default: 'var(--primary)',
    },
    // [{ label, icon?, active?, disabled?, onClick }]
    options: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // Só o ícone no gatilho, sem rótulo nem chevron — o desenho do composer do
    // modo limpo, onde os seletores viram círculos ao lado do enviar.
    //
    // ⚠️ O nome acessível do gatilho vem do `<span>{{ label }}</span>`. Sem ele
    // o botão fica MUDO para leitor de tela, então aqui o `label` migra para
    // `aria-label` + `title` — e o `title` devolve por tooltip a informação que
    // o rótulo dava a olho.
    // Tamanho do ícone do GATILHO. 13 é o dos chips com rótulo; no modo
    // só-ícone o ícone é a única coisa no botão e pede corpo.
    //
    // ⚠️ Sem esta declaração o `:size` do template vira `undefined` e o
    // `icon-svg` cai no PADRÃO de 24px — foi o que inchou o "+" e o agente de
    // uma vez, sem ninguém ter mexido no agente.
    tamanhoDoIcone: {
      type: Number,
      default: 13,
    },
    soIcone: {
      type: Boolean,
      default: false,
    },
    // Texto do tooltip quando `soIcone`. Vazio = usa o próprio `label`; o
    // composer manda o motivo da trava por aqui quando a conversa está travada.
    tooltip: {
      type: String,
      default: '',
    },
    // Lado em que o painel se alinha ao trigger — o dropdown do modelo fica
    // perto da borda direita do composer e não pode abrir pra fora da caixa.
    align: {
      type: String,
      default: 'left',
      // Só existe classe CSS (composer-inline-select__menu--left/--right)
      // pros dois valores abaixo — um typo aqui vira dropdown sem
      // posicionamento (nem left nem right), silenciosamente.
      validator: (value) => ['left', 'right'].includes(value),
    },
    // Lado para onde o painel abre. Default `up` porque este componente nasceu
    // na toolbar do composer, que fica na BASE da tela — abrir para baixo ali
    // jogaria o menu para fora do painel. No cabeçalho do histórico do popup é
    // o oposto: `up` encosta no header e o menu fica cortado, inalcançável.
    //
    // `auto` mede o espaço na hora de abrir. É o que o composer do chat precisa:
    // ele fica no MEIO da tela enquanto não há conversa e desce para o rodapé
    // depois da primeira mensagem — um lado fixo acerta um caso e erra o outro.
    direction: {
      type: String,
      default: 'up',
      // Mesmo risco do `align`: só há classe CSS para `up`/`down`, e um typo
      // vira menu sem posicionamento vertical, silenciosamente. `auto` nunca
      // chega ao CSS — vira um dos dois em `direcaoEfetiva`.
      validator: (value) => ['up', 'down', 'auto'].includes(value),
    },
    // Largura mínima do painel aberto, em px. Aditiva: o default reproduz o
    // `min-width: 180px` que estava fixo no CSS, então quem não passa nada
    // renderiza igual a antes.
    panelWidth: {
      type: Number,
      default: LARGURA_PAINEL_PADRAO,
    },
    /**
     * Painel na largura da CAIXA do composer, e não do gatilho.
     *
     * É o desenho do menu do "+": ele lista ferramentas com nome e descrição na
     * mesma linha, e num painel estreito a descrição vira reticência em quase
     * toda opção.
     *
     * ⚠️ Depende de a caixa hospedeira ser o contexto de posicionamento — hoje
     * `.send-message-inner`, que declara `position: relative` por causa disto. A
     * raiz deste componente vira `static` no modo (ver `--largura-total`),
     * senão o `left/right: 0` pararia no gatilho, que tem a largura de um ícone.
     */
    larguraTotal: {
      type: Boolean,
      default: false,
    },
    /**
     * Dica no pé do painel — "Digite para buscar…".
     *
     * Fica FORA da lista rolável de propósito: ela não é opção, e dentro da
     * rolagem seria lida como a última linha clicável. Vazio não renderiza
     * nada, que é o caso dos menus de agente e de modelo — listas curtas onde
     * não há o que buscar.
     */
    rodape: {
      type: String,
      default: '',
    },
    /**
     * O clique no gatilho NÃO abre o painel: emite `abrir-externo` e quem
     * escuta decide o que fazer.
     *
     * É como o chip de agente deixa de ter dropdown na `/ia` e passa a abrir o
     * palette. O visual do gatilho continua idêntico — muda só o que o clique
     * faz. `disabled` continua vencendo: chip travado não abre nada, nem aqui
     * nem lá.
     */
    abrirPorFora: {
      type: Boolean,
      default: false,
    },
    /**
     * O clique no GATILHO não rouba o foco — como o comando `/` (`abrir(false)`),
     * mas para quem abre pelo MOUSE. É o menu do "+": ele fica ao lado da
     * textarea do composer, e a pessoa pode querer continuar digitando (para
     * filtrar) sem precisar clicar de volta no campo, como o ChatGPT. Nos
     * demais gatilhos (modelo, agente, escopo) não há campo de texto ao lado
     * para voltar — ali o clique continua focando o primeiro item, como hoje.
     */
    manterFoco: {
      type: Boolean,
      default: false,
    },
    /**
     * Mostra o CHEVRON mesmo em `soIcone` — ícone do gatilho + um chevron
     * pequeno juntos, sem rótulo. É o desenho do menu do microfone (mockup do
     * Claude): sozinho, o ícone não avisa que há um menu por trás; com
     * `soIcone` puro o chevron sumia junto com o rótulo.
     *
     * Aditivo, default `false` — os consumidores atuais (agente, modelo,
     * escopo, ferramentas) continuam idênticos.
     */
    soIconeComChevron: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Se o foco entra na lista ao abrir. Falso quando o menu foi aberto
      // por um comando digitado — ver `abrir()`.
      focarAoAbrir: true,
      open: false,
      // Lado escolhido pela medição, quando `direction === 'auto'`.
      direcaoMedida: 'up',
      // Seleção VISUAL do primeiro item focável, independente do foco real do
      // DOM — ver `open` watcher. Existe porque `comFoco = false` (comando
      // `/`) deixa o foco de verdade na textarea de propósito, mas o usuário
      // ainda precisa VER qual item Enter/Tab vai confirmar, como no ChatGPT.
      highlightedIndex: -1,
    }
  },
  computed: {
    // O que de fato vai para o CSS. Com `auto`, o valor vem da medição feita
    // ao abrir; nos demais casos o pai mandou e é o pai que manda.
    direcaoEfetiva() {
      return this.direction === 'auto' ? this.direcaoMedida : this.direction
    },
    // Trigger fechado reflete a cor da opção selecionada (mesmo `--primary`
    // usado no item ativo dentro do menu) — sem isso, o botão ficava sempre
    // cinza neutro mesmo com uma persona/modelo já selecionado, e só o menu
    // aberto mostrava a cor. Só se aplica quando ninguém passou `icon-color`
    // explicitamente (ex: a marca "W" do seletor de modelo, que não segue
    // essa semântica de seleção).
    triggerIconColor() {
      if (this.iconColor) return this.iconColor
      const hasActiveOption = this.options.some((option) => option.active)
      return hasActiveOption ? this.activeColor : 'var(--neutral-400)'
    },
    // Inline em vez de classe: a largura é um número que o consumidor escolhe,
    // e uma classe por valor possível não escala.
    // No modo largura total quem manda é o `left/right: 0`; um `min-width` junto
    // impediria o painel de encolher com a caixa em tela estreita.
    estiloMenu() {
      if (this.larguraTotal) return {}
      return { 'min-width': this.panelWidth + 'px' }
    },
  },
  watch: {
    // Listener de ESC no document (mesmo padrão do click-outside-mixin,
    // ligado só enquanto o menu está aberto) — o `@keydown.esc` no template
    // só dispara com o foco dentro do subtree; isso cobre o caso do foco
    // ter saído dali por qualquer motivo.
    open(isOpen) {
      if (isOpen) {
        // Antes de qualquer coisa, e SEM `$nextTick`: o watcher roda antes do
        // Vue aplicar o patch, então a classe de lado já sai certa na primeira
        // pintura. Medindo depois, o menu apareceria embaixo e pularia pra cima.
        this.medirDirecao()
        document.addEventListener('keydown', this.onDocumentKeydown, true)
        // ⚠️ EM `$nextTick`, e não direto: o painel é `v-if="open"` e só entra
        // no DOM depois do Vue aplicar este patch — lido antes, `$refs.items`
        // ainda é do render ANTERIOR (vazio, no primeiro open) e
        // `primeiroFocavel()` sempre voltava -1. Era por isso que o `/` do
        // composer (sem foco real, `comFoco = false`) nunca destacava nada: o
        // caminho do clique "+" escondia o mesmo bug, porque `focusItemAt(0)`
        // corrige o índice como efeito colateral ao focar de verdade.
        this.$nextTick(() => {
          this.highlightedIndex = this.primeiroFocavel()
          if (this.focarAoAbrir) this.focusItemAt(0)
        })
      } else {
        document.removeEventListener('keydown', this.onDocumentKeydown, true)
        this.highlightedIndex = -1
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onDocumentKeydown, true)
  },
  methods: {
    /**
     * Decide o lado do painel pelo espaço que sobra abaixo do gatilho.
     *
     * Cabe embaixo, fica embaixo. Não cabendo, vai para o lado com MAIS espaço —
     * e não cegamente para cima, senão um gatilho colado no topo trocaria um
     * corte por outro.
     */
    medirDirecao() {
      if (this.direction !== 'auto') return
      const gatilho = this.$refs.trigger
      if (!gatilho) return

      const caixa = gatilho.getBoundingClientRect()
      const abaixo = window.innerHeight - caixa.bottom
      const acima = caixa.top
      const cabeAbaixo = abaixo >= ALTURA_MAXIMA_PAINEL
      this.direcaoMedida = cabeAbaixo || abaixo >= acima ? 'down' : 'up'
    },
    toggle() {
      if (this.disabled) return
      // Quem abre é outro: o painel local nem chega a montar.
      if (this.abrirPorFora) {
        this.$emit('abrir-externo')
        return
      }
      const abrindo = !this.open
      // Clique no gatilho: por padrão o foco DEVE ir para a lista, senão a
      // seta seguinte não teria de onde partir. `manterFoco` inverte isso
      // pro menu do "+" — ver a prop.
      this.focarAoAbrir = !this.manterFoco
      this.open = abrindo
      // O clique em si já deu foco NATIVO ao botão (comportamento padrão do
      // browser em `<button>`) — avisa quem tem um campo de texto ao lado
      // pra devolver o cursor pra lá, já que aqui ninguém vai focar a lista.
      if (abrindo && this.manterFoco) this.$emit('abriu-sem-foco')
    },
    /**
     * Abre sem alternar — para quem chama de fora. `toggle()` fecharia se já
     * estivesse aberto, que não é o que um comando de abrir deve fazer.
     *
     * ⚠️ `comFoco = false` é o caso do comando `/` do composer: ali a pessoa
     * está DIGITANDO, e roubar o foco para o primeiro item trava a digitação no
     * segundo caractere. O menu abre embaixo e o cursor fica onde está; quem
     * quiser navegar aperta a seta, e aí sim o foco entra na lista.
     */
    abrir(comFoco) {
      if (this.disabled || this.open) return
      this.focarAoAbrir = comFoco !== false
      this.open = true
    },
    // Fecha sem devolver o foco ao gatilho — quem chama de fora é o composer,
    // e o usuário está digitando no campo. O `close()` roubaria o cursor dali.
    fechar() {
      this.open = false
    },
    close() {
      // MUST DO do review do PR #1939 (rodada 4): devolver o foco
      // incondicionalmente rouba o foco de onde o usuário acabou de clicar
      // — este close() também é o handler de clique-fora (onClickOutside,
      // capture-phase no document), disparado DEPOIS do mousedown já ter
      // movido o foco pro elemento clicado (ex: a textarea do composer,
      // logo abaixo deste dropdown na toolbar). Só devolver o foco quando
      // ele ainda estava dentro do próprio dropdown — ou seja, fechamento
      // por ESC ou seleção via teclado, nunca por clique-fora.
      const focoEstavaDentro =
        this.$el && this.$el.contains(document.activeElement)
      this.open = false
      if (focoEstavaDentro && this.$refs.trigger) this.$refs.trigger.focus()
    },
    isOpen() {
      return this.open
    },
    isClickOutside(target) {
      return this.open && this.$el && !this.$el.contains(target)
    },
    onClickOutside() {
      this.close()
    },
    /**
     * Teclado enquanto o menu está aberto. Ligado no `watch: open`, em fase de
     * CAPTURA no document.
     *
     * ⚠️ As setas precisam estar AQUI, e não só no `@keydown` do painel. Com o
     * menu aberto por clique de mouse o foco pode estar no gatilho — que é
     * irmão do painel, não filho —, e ali o keydown nunca passa por ele. Foi o
     * que fez a navegação por teclado não funcionar na prática, mesmo com os
     * handlers do template no lugar.
     *
     * ⚠️ E as setas saíram do template por isso: com as duas vias ligadas, cada
     * tecla era tratada DUAS vezes e o foco pulava de dois em dois itens.
     */
    onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        this.close()
        return
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        // ⚠️ `stopPropagation`, e não só `preventDefault`: com o menu aberto
        // pelo comando `/` (`comFoco = false`), o foco de VERDADE continua na
        // textarea — o alvo do evento não está dentro do `[role="menu"]`, e o
        // `emMenu` do `send-message` não o reconhece. Sem cortar aqui, a MESMA
        // seta também chegava ao capture-listener de lá e disparava
        // `descer-para-acoes` (cursor no fim do campo), que competia com a
        // navegação do menu — a primeira seta parecia não fazer nada.
        event.stopPropagation()
        this.moveFocus(1)
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        event.stopPropagation()
        this.moveFocus(-1)
        return
      }
      // TAB confirma em vez de navegar — e Enter confirma mesmo quando o foco
      // de verdade ainda está no campo (comando `/`, `comFoco = false`): sem
      // isto o Enter chegava ao `onKeydown` do composer e enviava a mensagem
      // em vez de escolher a ferramenta destacada.
      if (event.key === 'Enter' || event.key === 'Tab') this.confirmarAtual(event)
    },
    confirmarAtual(event) {
      const item = this.itemAtual()
      if (!item) return
      event.preventDefault()
      // Impede o capture-listener do `send-message` (Enter → enviar) de rodar
      // em seguida — os dois estão em fase de CAPTURA no document e no `$el`,
      // nessa ordem, e só `stopPropagation` interrompe o segundo.
      event.stopPropagation()
      item.click()
    },
    /**
     * O item que Enter/Tab confirmam agora: o realmente focado, ou — sem foco
     * real dentro do menu (comando `/`) — o destacado visualmente.
     *
     * ⚠️ **Nunca um item DESABILITADO** (G-2 do QA, 19/09/2026). O
     * `highlightedIndex` é calculado uma vez, no `watch` de `open`; o filtro
     * digitado depois pode reduzir a lista à linha "Nenhuma ferramenta com
     * esse nome", que é desabilitada — e o índice velho passava a apontar
     * para ela. `confirmarAtual` então cortava a propagação e clicava num
     * botão desabilitado: no-op. Medido na `/home-ia`, digitando
     * `/pré-reunião Ilo Santos`: **zero POST** em 125 s, com o texto intacto
     * no campo. Item que `select()` já recusa (`if (option.disabled) return`)
     * não pode sequestrar a tecla para não fazer nada.
     */
    itemAtual() {
      const items = this.$refs.items || []
      if (!items.length) return null
      if (items.indexOf(document.activeElement) !== -1) {
        return document.activeElement
      }
      const destacado = items[this.highlightedIndex]
      if (!destacado || destacado.disabled) return null
      return destacado
    },
    select(option) {
      if (option.disabled) return
      this.close()
      if (typeof option.onClick === 'function') {
        option.onClick()
      }
    },
    // Navegação por seta (↑/↓/Home/End) entre os itens do menu — role=menu
    // do wrapper + role=menuitem de cada botão declaram a semântica; isto
    // move o foco de fato entre eles.
    // Pula os DESABILITADOS: no menu do "+" o primeiro item é o anexo, que
    // está bloqueado. Botão desabilitado não recebe foco, então parar nele
    // deixava a seta sem efeito nenhum.
    // Último item FOCÁVEL — com desabilitados no meio, não é `length - 1`.
    focarUltimo() {
      const items = this.$refs.items || []
      if (items.length) this.focusItemAt(items.length - 1, -1)
    },
    /**
     * Índice focável mais próximo de `index`, andando na direção de `passo` —
     * pula os DESABILITADOS (no menu do "+" o primeiro item é o anexo,
     * bloqueado enquanto o envio não tem canal para arquivo). Só CALCULA;
     * quem decide se isso vira foco real ou só destaque visual é quem chama.
     */
    proximoIndiceFocavel(index, passo) {
      const items = this.$refs.items || []
      if (!items.length) return -1
      const direcao = passo || 1
      let i = index
      for (let tentativas = 0; tentativas < items.length; tentativas += 1) {
        const posicao = (i + items.length) % items.length
        const el = items[posicao]
        if (el && !el.disabled) return posicao
        i += direcao
      }
      return -1
    },
    focusItemAt(index, passo) {
      const items = this.$refs.items || []
      const posicao = this.proximoIndiceFocavel(index, passo)
      if (posicao === -1) return
      items[posicao].focus()
      // Sincroniza o destaque visual com o foco real — sem isto, um item
      // ficava marcado por `--highlighted` enquanto outro tinha `:focus`,
      // os dois com o mesmo fundo, e a lista parecia ter duas seleções.
      this.highlightedIndex = posicao
    },
    /**
     * Índice do primeiro item que pode receber foco.
     *
     * ⚠️ Não é `0`: no menu do "+" o primeiro é o anexo, que fica desabilitado
     * enquanto o envio não tem canal para arquivo. Sem isto, "estou no topo?"
     * daria falso na única configuração em que a pergunta importa.
     */
    primeiroFocavel() {
      return this.proximoIndiceFocavel(0, 1)
    },
    /**
     * Rola o item destacado para dentro da área visível da lista — o
     * equivalente manual do que `el.focus()` já faz sozinho quando o foco é
     * real (ver `moverDestaque`, que nunca chama `.focus()`).
     */
    rolarAteDestacado() {
      this.$nextTick(() => {
        const items = this.$refs.items || []
        const el = items[this.highlightedIndex]
        const lista = this.$refs.lista
        if (!el || !lista) return
        // Só a `__lista` (o único `overflow-y: auto` do menu) rola — o
        // "nearest" é feito à mão por rects. `scrollIntoView` subiria a cadeia
        // de ancestrais e, em painel alto, empurrava a área do chat.
        const caixa = lista.getBoundingClientRect()
        const item = el.getBoundingClientRect()
        if (item.top < caixa.top) lista.scrollTop += item.top - caixa.top
        else if (item.bottom > caixa.bottom) lista.scrollTop += item.bottom - caixa.bottom
      })
    },
    /**
     * Move só o destaque VISUAL, sem tocar no foco real do DOM — usada quando
     * o menu abriu sem roubar o cursor (comando `/`, `comFoco = false`). É o
     * que deixa a pessoa DIGITAR o tempo todo enquanto navega com a seta,
     * como o ChatGPT: mover o foco de verdade para um `<button>` do menu
     * tiraria o cursor da textarea a cada seta, e ela parava de aceitar
     * caractere até a seta para cima "devolver" o foco.
     */
    moverDestaque(delta) {
      const items = this.$refs.items || []
      if (!items.length) return
      const current = this.highlightedIndex
      if (current === -1) {
        this.highlightedIndex = this.proximoIndiceFocavel(
          delta > 0 ? 0 : items.length - 1,
          delta
        )
        this.rolarAteDestacado()
        return
      }
      // Mesma regra do foco real: subir além do primeiro sai do menu (ver
      // `moveFocus`) em vez de dar a volta pelo fim.
      const subindoDoTopo = delta < 0 && current === this.primeiroFocavel()
      if (subindoDoTopo && this.$listeners['sair-pelo-topo']) {
        this.$emit('sair-pelo-topo')
        return
      }
      this.highlightedIndex = this.proximoIndiceFocavel(current + delta, delta)
      this.rolarAteDestacado()
    },
    moveFocus(delta) {
      // Sem foco real a mover (comando `/`): navegação vira só destaque, pelo
      // motivo documentado em `moverDestaque`.
      if (!this.focarAoAbrir) {
        this.moverDestaque(delta)
        return
      }
      const items = this.$refs.items || []
      if (!items.length) return
      const current = items.indexOf(document.activeElement)
      // Nada focado ainda (abriu e apertou seta direto): pula pro extremo na
      // direção da seta, em vez de tentar "avançar" a partir de um índice
      // que não existe.
      if (current === -1) {
        this.focusItemAt(delta > 0 ? 0 : items.length - 1, delta)
        return
      }
      // ★ Subir além do primeiro DEVOLVE o foco a quem abriu, em vez de dar a
      // volta pelo fim. Quem entrou no menu com a seta para baixo, vindo do
      // campo, espera sair por onde entrou — e o ciclo prendia a pessoa lá
      // dentro: a seta para cima no topo pulava para o último item.
      //
      // ⚠️ Só quando alguém ESCUTA. Sem listener o ciclo continua valendo: num
      // menu aberto pelo próprio gatilho (o chip de agente, por exemplo) não há
      // campo para onde voltar, e sair pelo topo deixaria o foco em lugar
      // nenhum — pior que dar a volta.
      const subindoDoTopo =
        delta < 0 && current === this.primeiroFocavel()
      if (subindoDoTopo && this.$listeners['sair-pelo-topo']) {
        this.$emit('sair-pelo-topo')
        return
      }
      this.focusItemAt((current + delta + items.length) % items.length, delta)
    },
  },
}
</script>

<template>
  <div
    class="composer-inline-select"
    :class="{ 'composer-inline-select--largura-total': larguraTotal }"
    @keydown.esc="close"
  >
    <button
      ref="trigger"
      type="button"
      class="composer-inline-select__trigger ia-texto-auxiliar"
      :class="{ 'composer-inline-select__trigger--open': open }"
      :disabled="disabled"
      aria-haspopup="true"
      :aria-expanded="String(open)"
      :aria-label="soIcone ? label : null"
      :title="soIcone ? tooltip || label : null"
      @click="toggle"
    >
      <icon-svg
        v-if="icon"
        :name="icon"
        :size="tamanhoDoIcone"
        :color="triggerIconColor"
      />
      <span v-if="!soIcone">{{ label }}</span>
      <icon-svg
        v-if="!soIcone || soIconeComChevron"
        name="mdi-chevron-down"
        :size="soIcone ? 10 : 13"
        color="var(--neutral-400)"
        class="composer-inline-select__chevron"
        :class="{
          'composer-inline-select__chevron--open': open,
          'composer-inline-select__chevron--compacto': soIcone,
        }"
      />
    </button>

    <div
      v-if="open"
      class="composer-inline-select__menu"
      :class="[
        `composer-inline-select__menu--${direcaoEfetiva}`,
        larguraTotal
          ? 'composer-inline-select__menu--largura-total'
          : `composer-inline-select__menu--${align}`,
      ]"
      :style="estiloMenu"
      role="menu"
      @keydown.home.prevent="focusItemAt(0)"
      @keydown.end.prevent="focarUltimo"
    >
      <div ref="lista" class="composer-inline-select__lista">
        <button
          v-for="(option, index) in options"
          ref="items"
          :key="index"
          type="button"
          role="menuitem"
          class="composer-inline-select__item ia-texto-corpo"
          :class="{
            'composer-inline-select__item--active': option.active,
            'composer-inline-select__item--highlighted':
              index === highlightedIndex,
          }"
          :disabled="option.disabled"
          :aria-pressed="String(!!option.active)"
          @click="select(option)"
        >
          <icon-svg
            v-if="option.icon"
            :name="option.icon"
            :size="16"
            :color="option.active ? activeColor : 'var(--neutral-400)'"
          />
          <span class="composer-inline-select__item-text">
            <span class="composer-inline-select__item-label">
              {{ option.label }}
            </span>
            <span
              v-if="option.description"
              class="composer-inline-select__item-description ia-texto-auxiliar"
            >
              {{ option.description }}
            </span>
          </span>
          <icon-svg
            v-if="option.active"
            name="mdi-check"
            :color="activeColor"
            :size="14"
            class="composer-inline-select__check"
          />
        </button>
      </div>

      <!-- Fora da lista: fica fixo enquanto ela rola, e `aria-hidden` porque é
           instrução de uso repetida, não conteúdo do menu — o leitor de tela já
           anuncia o campo e as opções. -->
      <p v-if="rodape" class="composer-inline-select__rodape ia-texto-auxiliar" aria-hidden="true">
        {{ rodape }}
      </p>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.composer-inline-select
  position: relative
  flex-shrink: 0

  // `static` de propósito: no modo largura total o painel se mede pela CAIXA do
  // composer (`.send-message-inner`, que é `relative`), e uma raiz posicionada
  // aqui o prenderia à largura do gatilho — um ícone.
  &--largura-total
    position: static

.composer-inline-select__trigger
  display: inline-flex
  align-items: center
  gap: 4px
  padding: 3px 6px
  border: 0
  border-radius: 6px
  background: transparent
  color: var(--neutral-900)
  cursor: pointer
  transition: background 0.12s

  &:hover,
  &--open
    background: var(--neutral-100)

  &:disabled
    cursor: not-allowed
    opacity: 0.5

.composer-inline-select__chevron
  transition: transform 0.15s ease
  flex-shrink: 0

  &--open
    transform: rotate(180deg)

  // `soIconeComChevron`: chevron pequeno colado no ícone, dentro de um
  // gatilho circular — o `gap: 4px` do trigger já dá o respiro entre os dois.
  &--compacto
    margin-left: -2px

.composer-inline-select__menu
  position: absolute
  // O eixo vertical vem de `--up`/`--down` (prop `direction`); nada de offset
  // fixo aqui, senão as duas regras coexistiriam e o menu esticaria.
  // min-width vem do inline `estiloMenu` (prop panelWidth) — mantê-lo aqui
  // também criaria duas fontes para o mesmo valor.
  background: var(--white)
  border: 1px solid var(--border-hairline)
  // 20 e não 10: o painel abre colado na caixa do composer, que tem 24 — com 10
  // ele lia como um card de outro sistema pousado sob um campo arredondado. Um
  // ponto abaixo do 24 porque o menu é mais alto que a caixa, e o mesmo raio num
  // retângulo alto pesa mais.
  // ⚠️ Mesmo valor em `design/_escopo-select-menu.sass`. Mudou aqui, mude lá.
  border-radius: 20px
  box-shadow: var(--shadow-pop)
  // 8 e não 4: com 4 o primeiro item encostava na borda de cima. Mesmo valor
  // da casca do menu de cliente (`design/_escopo-select-menu.sass`) — os dois
  // abrem do mesmo composer e precisam ser a mesma coisa.
  // Sem padding aqui: o cabeçalho encosta nas bordas para a faixa quente ir de
  // ponta a ponta, como no popover de fontes. Quem respira é a lista.
  padding: 0
  overflow: hidden
  z-index: 20
  // ⚠️ Teto obrigatório desde o layout em LINHA. O painel é `absolute` e só
  // tinha `min-width`, então dimensionava pelo conteúdo: com rótulo e descrição
  // na mesma linha, a opção mais longa esticava o menu para fora da janela e
  // punha scroll horizontal na PÁGINA (visto no menu de agente). É este teto
  // que faz a reticência da descrição entrar em ação.
  max-width: 420px

  &--left
    left: 0

  &--right
    right: 0

  // Colado nas duas bordas da caixa: é o `left`/`right` que dá a largura, e não
  // um `width` — assim o painel acompanha o composer quando a coluna encolhe.
  // `max-width: none` porque aqui quem limita é a caixa, e o teto de 420 do
  // caso geral cortaria o painel no meio do composer.
  &--largura-total
    left: 0
    right: 0
    max-width: none

  // 10 e não 6: com 6 o painel encostava na caixa e os dois liam como uma peça
  // só. O respiro é o que diz que o menu está SOBRE o composer, não dentro.
  &--up
    bottom: calc(100% + 10px)

  &--down
    top: calc(100% + 10px)

.composer-inline-select__lista
  padding: 8px
  // Teto igual ao da lista de fontes: acima disto o painel passa da dobra e
  // vira uma página dentro do menu.
  max-height: 280px
  overflow-y: auto

.composer-inline-select__item
  color: var(--neutral-900)
  display: flex
  align-items: center
  // Escala do item: era 7px/9px de padding com 13px de fonte, herdados de quando
  // o painel tinha a largura do gatilho. Na largura da caixa aquilo virava uma
  // lista miúda dentro de muito espaço — o alvo de clique não acompanhou o
  // painel. Aqui e no `:size` do ícone (template) está o tamanho da linha.
  gap: 10px
  width: 100%
  padding: 10px 12px
  border: 0
  background: none
  border-radius: 8px
  text-align: left
  cursor: pointer

  // Foco lê IGUAL ao hover — a seta e o mouse apontam a mesma coisa, então
  // mostram a mesma coisa. Um anel de contorno aqui grita num menu deste tamanho.
  //
  // `:focus` e NÃO `:focus-visible`: o foco chega por script (`focusItemAt` no
  // `watch: open`). Aberto o menu com o MOUSE, o Chrome não considera o foco
  // programático "visível" e a heurística do `:focus-visible` não casa — o item
  // ficava focado sem destaque nenhum, que é o que fazia a navegação por seta
  // parecer quebrada mesmo funcionando.
  &:hover,
  &:focus,
  // Marca visual do item corrente sem foco real do DOM — o comando `/` do
  // composer não move o foco (a pessoa está digitando), mas precisa mostrar
  // qual item Enter/Tab vão confirmar. Mesmo visual do foco/hover de propósito.
  &--highlighted
    background: var(--neutral-100)

  &:disabled
    cursor: not-allowed
    opacity: 0.5

  // Ativo se distingue pelo CHECK e pelo ícone em `activeColor`, não por peso:
  // desde o layout em linha o rótulo já é 600 em toda opção, e engrossar aqui
  // engrossaria a DESCRIÇÃO junto — o oposto da hierarquia que ela tem.
  &--active
    color: var(--neutral-900)

// Rótulo e descrição na MESMA linha, e não empilhados: cada opção é uma frase
// só — "Criar imagem · disponível às 13:02" —, e empilhar dobrava a altura do
// menu para dizer o mesmo. O peso separa o nome do complemento; a cor reforça.
.composer-inline-select__item-text
  display: flex
  align-items: baseline
  gap: 8px
  flex: 1
  min-width: 0

// Nunca encolhe nem quebra: o NOME é o que se procura na lista. Quem cede
// espaço é a descrição.
.composer-inline-select__item-label
  font-weight: 600
  white-space: nowrap
  flex-shrink: 0

.composer-inline-select__item-description
  color: var(--neutral-600)
  // Corta com reticência em vez de quebrar: numa lista de linhas de altura fixa
  // a quebra desalinharia só a opção de descrição longa.
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis
  min-width: 0

.composer-inline-select__check
  margin-left: auto
  flex-shrink: 0

// Sem borda: o que separa a dica da lista é o ESPAÇO, não uma linha — com ela o
// painel ganhava um rodapé de caixa de diálogo e parecia ter duas partes.
//
// `padding-left` de 20 e não 12 para o texto começar na mesma coluna do
// conteúdo dos itens: a lista tem 8 de padding e o item mais 12, e com 12 aqui a
// dica ficava recuada à esquerda de tudo.
//
// Fonte igual à da `__item-description` (12px): é o mesmo registro — texto de
// apoio, não conteúdo.
.composer-inline-select__rodape
  color: var(--neutral-600)
  margin: 0
  padding: 4px 12px 12px 20px
</style>
